/**
 * ValidationController - Validation management for wizard forms
 *
 * Provides built-in validators and custom validator support.
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit';

import type { Validator, ValidatorFn, ValidationResult } from '../types.js';

// Default blocked email domains for work email validation
const DEFAULT_BLOCKED_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'live.com',
  'msn.com',
  'me.com',
  'inbox.com',
  'gmx.com',
];

export class ValidationController implements ReactiveController {
  host: ReactiveControllerHost;

  private _validators: Map<string, Validator[]> = new Map();

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  // ============================================
  // Lifecycle
  // ============================================

  hostConnected(): void {
    // Controller connected
  }

  hostDisconnected(): void {
    // Clean up
    this._validators.clear();
  }

  // ============================================
  // Validator Management
  // ============================================

  addValidator(name: string, validator: Validator): void {
    const existing = this._validators.get(name) ?? [];
    this._validators.set(name, [...existing, validator]);
  }

  addValidators(name: string, validators: Validator[]): void {
    validators.forEach((v) => this.addValidator(name, v));
  }

  removeValidator(name: string, validatorName: string): void {
    const existing = this._validators.get(name) ?? [];
    this._validators.set(
      name,
      existing.filter((v) => v.name !== validatorName)
    );
  }

  clearValidators(name: string): void {
    this._validators.delete(name);
  }

  getValidators(name: string): Validator[] {
    return this._validators.get(name) ?? [];
  }

  // ============================================
  // Validation Execution
  // ============================================

  async validate(name: string, value: unknown): Promise<ValidationResult> {
    const validators = this.getValidators(name);

    for (const validator of validators) {
      try {
        const result = await validator.validate(value);
        if (!result.valid) {
          return {
            valid: false,
            error: result.error ?? validator.message ?? 'Validation failed',
          };
        }
      } catch (error) {
        console.error(`[ValidationController] Validator "${validator.name}" threw:`, error);
        return {
          valid: false,
          error: 'Validation error occurred',
        };
      }
    }

    return { valid: true };
  }

  validateSync(name: string, value: unknown): ValidationResult {
    const validators = this.getValidators(name);

    for (const validator of validators) {
      const result = validator.validate(value);

      // If it's a promise, we can't handle it synchronously
      if (result instanceof Promise) {
        console.warn(`[ValidationController] Async validator "${validator.name}" called synchronously`);
        continue;
      }

      if (!result.valid) {
        return {
          valid: false,
          error: result.error ?? validator.message ?? 'Validation failed',
        };
      }
    }

    return { valid: true };
  }

  // ============================================
  // Built-in Validators (Static Factory Methods)
  // ============================================

  static required(message = 'This field is required'): Validator {
    return {
      name: 'required',
      message,
      validate: (value: unknown): ValidationResult => {
        if (value === null || value === undefined) {
          return { valid: false, error: message };
        }
        if (typeof value === 'string' && value.trim() === '') {
          return { valid: false, error: message };
        }
        if (Array.isArray(value) && value.length === 0) {
          return { valid: false, error: message };
        }
        return { valid: true };
      },
    };
  }

  static email(message = 'Please enter a valid email address'): Validator {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return {
      name: 'email',
      message,
      validate: (value: unknown): ValidationResult => {
        if (typeof value !== 'string' || value.trim() === '') {
          return { valid: true }; // Let required validator handle empty
        }
        if (!emailRegex.test(value)) {
          return { valid: false, error: message };
        }
        return { valid: true };
      },
    };
  }

  static workEmail(
    blockedDomains: string[] = DEFAULT_BLOCKED_DOMAINS,
    message = 'Please use your work email address'
  ): Validator {
    return {
      name: 'workEmail',
      message,
      validate: (value: unknown): ValidationResult => {
        if (typeof value !== 'string' || value.trim() === '') {
          return { valid: true }; // Let required validator handle empty
        }

        const domain = value.split('@')[1]?.toLowerCase();
        if (domain && blockedDomains.includes(domain)) {
          return { valid: false, error: message };
        }
        return { valid: true };
      },
    };
  }

  static pattern(regex: RegExp, message = 'Invalid format'): Validator {
    return {
      name: 'pattern',
      message,
      validate: (value: unknown): ValidationResult => {
        if (typeof value !== 'string' || value.trim() === '') {
          return { valid: true }; // Let required validator handle empty
        }
        if (!regex.test(value)) {
          return { valid: false, error: message };
        }
        return { valid: true };
      },
    };
  }

  static minLength(min: number, message?: string): Validator {
    const errorMessage = message ?? `Minimum ${min} characters required`;

    return {
      name: 'minLength',
      message: errorMessage,
      validate: (value: unknown): ValidationResult => {
        if (typeof value !== 'string') {
          return { valid: true };
        }
        if (value.length < min) {
          return { valid: false, error: errorMessage };
        }
        return { valid: true };
      },
    };
  }

  static maxLength(max: number, message?: string): Validator {
    const errorMessage = message ?? `Maximum ${max} characters allowed`;

    return {
      name: 'maxLength',
      message: errorMessage,
      validate: (value: unknown): ValidationResult => {
        if (typeof value !== 'string') {
          return { valid: true };
        }
        if (value.length > max) {
          return { valid: false, error: errorMessage };
        }
        return { valid: true };
      },
    };
  }

  static minSelections(min: number, message?: string): Validator {
    const errorMessage = message ?? `Select at least ${min} option${min > 1 ? 's' : ''}`;

    return {
      name: 'minSelections',
      message: errorMessage,
      validate: (value: unknown): ValidationResult => {
        if (!Array.isArray(value)) {
          return { valid: true };
        }
        if (value.length < min) {
          return { valid: false, error: errorMessage };
        }
        return { valid: true };
      },
    };
  }

  static maxSelections(max: number, message?: string): Validator {
    const errorMessage = message ?? `Select at most ${max} option${max > 1 ? 's' : ''}`;

    return {
      name: 'maxSelections',
      message: errorMessage,
      validate: (value: unknown): ValidationResult => {
        if (!Array.isArray(value)) {
          return { valid: true };
        }
        if (value.length > max) {
          return { valid: false, error: errorMessage };
        }
        return { valid: true };
      },
    };
  }

  static custom(name: string, validateFn: ValidatorFn, message?: string): Validator {
    return {
      name,
      message,
      validate: validateFn,
    };
  }
}

// Export default blocked domains for external use
export { DEFAULT_BLOCKED_DOMAINS };
